import React from 'react';

const PEERS_CACHE_KEY = 'ovr_peers_cache_v1';

/**
 * Load cached peer addresses from localStorage.
 */
export function loadCachedPeers() {
  try {
    const raw = localStorage.getItem(PEERS_CACHE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return data.peers || [];
  } catch {
    return [];
  }
}

/**
 * Save peer addresses to localStorage.
 */
export function saveCachedPeers(peers) {
  try {
    localStorage.setItem(PEERS_CACHE_KEY, JSON.stringify({
      peers,
      ts: Date.now(),
    }));
  } catch {
    // localStorage may be full or unavailable
  }
}

/**
 * Pick a usable LAN address from a peer's addresses list.
 * Excludes Tailscale CGNAT addresses (100.64-127.x.x) entirely.
 */
function pickAddress(peer) {
  const addrs = peer.addresses || [];
  for (const a of addrs) {
    const parts = (a.ip || '').split('.');
    if (parts.length === 4 && parts[0] === '100') {
      const second = parseInt(parts[1], 10);
      if (second >= 64 && second <= 127) continue;
    }
    return a.ip;
  }
  return null;
}

/**
 * FailoverBanner — shown when the home node is unreachable and cached
 * peer data exists, providing clickable links to peer nodes' PWAs.
 */
export default function FailoverBanner({ activePeer, onDismiss }) {
  const cachedPeers = loadCachedPeers();

  // If connected via a peer, show that status
  if (activePeer) {
    return (
      <div className="failover-banner failover-banner-peer">
        <span>Connected via <strong>{activePeer.nodeId}</strong> — home node may need attention.</span>
        {onDismiss && (
          <button className="failover-dismiss" onClick={onDismiss} aria-label="Dismiss">x</button>
        )}
      </div>
    );
  }

  // No cached peers — nothing to show
  if (cachedPeers.length === 0) return null;

  const peerLinks = cachedPeers
    .map(p => {
      const ip = pickAddress(p);
      if (!ip) return null;
      const name = p.name || p.node_id || ip;
      return { name, ip, url: `http://${ip}:8080/` };
    })
    .filter(Boolean);

  if (peerLinks.length === 0) return null;

  return (
    <div className="failover-banner">
      <span>Home node unreachable. Try: </span>
      {peerLinks.map((link, i) => (
        <span key={link.ip}>
          {i > 0 && ' | '}
          <a href={link.url} className="failover-link">
            {link.name} ({link.ip})
          </a>
        </span>
      ))}
    </div>
  );
}
